import * as Sentry from '@sentry/react';
const QUOTA_WARNING_PERCENT = 80;
const LARGE_WRITE_BYTES = 10 * 1024 * 1024;
const HEAP_WARNING_PERCENT = 70;
// DEBUG flag — when true, every memory canary sample is shipped as an
// info-level Sentry event immediately, instead of being buffered as a
// breadcrumb that would be lost if the renderer is SIGKILL'd mid-task.
// Kept off in normal operation; flip on when investigating crashes that
// take the renderer down before any other Sentry event can flush.
const FORCE_TRANSPORT_MEMORY_CANARY = false;
let highQuotaCaptured = false;
let largeWriteCaptured = false;
let highHeapCaptured = false;
export const reportQuotaCanary = async (context) => {
    if (typeof navigator === 'undefined' || !navigator.storage?.estimate) {
        return;
    }
    try {
        const { usage, quota } = await navigator.storage.estimate();
        const percent = usage && quota ? (usage / quota) * 100 : null;
        Sentry.addBreadcrumb({
            category: 'storage',
            message: 'quota.estimate',
            level: 'info',
            data: { context, usage, quota, percent },
        });
        if (percent !== null && percent >= QUOTA_WARNING_PERCENT && !highQuotaCaptured) {
            highQuotaCaptured = true;
            Sentry.captureMessage('Storage quota high', {
                level: 'warning',
                contexts: { storage: { context, usage, quota, percent } },
            });
        }
    }
    catch {
        // navigator.storage.estimate() can throw in some private/incognito contexts; ignore.
    }
};
export const reportMemoryCanary = (context) => {
    // performance.memory is non-standard, Chromium-only (Chrome, Edge, Android
    // WebView). On other browsers it's undefined — silently no-op.
    const memory = performance.memory;
    if (!memory || !memory.jsHeapSizeLimit) {
        return;
    }
    const { usedJSHeapSize, totalJSHeapSize, jsHeapSizeLimit } = memory;
    const percent = (usedJSHeapSize / jsHeapSizeLimit) * 100;
    const memoryContext = {
        context,
        usedJSHeapSize,
        totalJSHeapSize,
        jsHeapSizeLimit,
        percent: Math.round(percent * 10) / 10,
    };
    Sentry.addBreadcrumb({
        category: 'memory',
        message: 'heap.estimate',
        level: 'info',
        data: memoryContext,
    });
    if (FORCE_TRANSPORT_MEMORY_CANARY) {
        // Ship the snapshot immediately so it survives a SIGKILL that happens
        // right after this point (e.g. inside the next imageCompression call).
        Sentry.captureMessage(`memory.canary:${context}`, {
            level: 'info',
            contexts: { memory: memoryContext },
        });
    }
    if (percent >= HEAP_WARNING_PERCENT && !highHeapCaptured) {
        highHeapCaptured = true;
        Sentry.captureMessage('Memory pressure high', {
            level: 'warning',
            contexts: { memory: memoryContext },
        });
    }
};
export const reportIdbWriteSize = (storeName, key, sizeBytes) => {
    Sentry.addBreadcrumb({
        category: 'idb',
        message: 'put',
        level: 'info',
        data: { storeName, key, sizeBytes },
    });
    if (sizeBytes > LARGE_WRITE_BYTES && !largeWriteCaptured) {
        largeWriteCaptured = true;
        Sentry.captureMessage('Large IDB write', {
            level: 'warning',
            contexts: { idb: { storeName, key, sizeBytes } },
        });
    }
};
export const approxJsonSize = (value) => {
    try {
        return new Blob([JSON.stringify(value)]).size;
    }
    catch {
        return 0;
    }
};
export const dataUrlToBlob = (dataUrl) => {
    const commaIdx = dataUrl.indexOf(',');
    if (commaIdx === -1) {
        return new Blob([dataUrl]);
    }
    const header = dataUrl.slice(0, commaIdx);
    const payload = dataUrl.slice(commaIdx + 1);
    const mimeMatch = header.match(/^data:([^;]+)/);
    const mimeType = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
    const isBase64 = header.includes(';base64');
    if (!isBase64) {
        return new Blob([decodeURIComponent(payload)], { type: mimeType });
    }
    const binary = atob(payload);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return new Blob([bytes], { type: mimeType });
};
export const mimeTypeFromDataUrl = (dataUrl) => {
    const match = dataUrl.match(/^data:([^;,]+)/);
    return match ? match[1] : 'application/octet-stream';
};
export const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
        if (typeof reader.result === 'string') {
            resolve(reader.result);
        }
        else {
            reject(new Error('FileReader returned non-string result'));
        }
    };
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
    reader.readAsDataURL(blob);
});
